import { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Product, ProductVariant } from '../../../hooks/useProducts';
import { useCart } from '../../../contexts/CartContext';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { SITE_ID } from '../../../lib/constants';

interface ProductInfoProps {
  product: Product;
}

export default function ProductInfo({ product }: ProductInfoProps) {
  const { addItem } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [wished, setWished] = useState(false);
  const [favLoading, setFavLoading] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);

  // Kullanıcı giriş yapmışsa favori durumunu kontrol et
  useEffect(() => {
    if (!user) return;
    supabase
      .from('favoriler')
      .select('id')
      .eq('site_id', SITE_ID)
      .eq('user_id', user.id)
      .eq('urun_id', product.id)
      .maybeSingle()
      .then(({ data }) => setWished(!!data));
  }, [user, product.id]);

  const toggleFavorite = async () => {
    if (!user) {
      // Giriş yapmamışsa sadece UI değişimi (veya login modal açılabilir)
      setWished(!wished);
      return;
    }
    setFavLoading(true);
    if (wished) {
      // Favoriden çıkar
      await supabase
        .from('favoriler')
        .delete()
        .eq('site_id', SITE_ID)
        .eq('user_id', user.id)
        .eq('urun_id', product.id);
      setWished(false);
    } else {
      // Favoriye ekle
      await supabase.from('favoriler').insert({
        site_id: SITE_ID,
        user_id: user.id,
        urun_id: product.id,
      });
      setWished(true);
    }
    setFavLoading(false);
  };

  const variants = product.varyantlar || [];
  const hasVariants = variants.length > 0;

  // Varyant özellik gruplarını çıkar (örn: { "Renk": ["Siyah","Beyaz"], "Beden": ["S","M","L"] })
  const variantGroups = useMemo(() => {
    const groups: Record<string, string[]> = {};
    variants.forEach(v => {
      Object.entries(v.ozellikler).forEach(([key, val]) => {
        if (!groups[key]) groups[key] = [];
        if (!groups[key].includes(val)) groups[key].push(val);
      });
    });
    return groups;
  }, [variants]);

  // Seçili özellik değerleri state'i
  const [selectedAttrs, setSelectedAttrs] = useState<Record<string, string>>({});

  // Varyasyonlu ürün açıldığında kendi varyantını otomatik seç
  // Strateji: varyant_adi veya ozellikler değeri ürün adında geçiyorsa o varyantı seç
  useEffect(() => {
    if (!hasVariants || variants.length === 0) return;

    const productNameLower = product.name.toLowerCase();
    const productSlugLower = product.slug.toLowerCase();

    // Bu ürüne karşılık gelen varyantı bul
    const matchingVariant = variants.find(v => {
      // Varyant adı ürün adında geçiyor mu?
      const vAdLower = v.varyant_adi.toLowerCase();
      if (productNameLower.includes(vAdLower) || productSlugLower.includes(vAdLower.replace(/\s+/g, '-').replace(/[()]/g, ''))) {
        return true;
      }
      // Varyant ozellikler değerlerinden biri ürün adında geçiyor mu?
      return Object.values(v.ozellikler).some(val => {
        const valLower = val.toLowerCase();
        return productNameLower.includes(valLower) || productSlugLower.includes(valLower.replace(/\s+/g, '-').replace(/[()]/g, ''));
      });
    });

    if (matchingVariant) {
      // Bu varyantın ozellikler değerlerini seçili yap
      const autoAttrs: Record<string, string> = { ...matchingVariant.ozellikler };
      setSelectedAttrs(autoAttrs);
      setSelectedVariant(matchingVariant);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id, variants.length]);

  // Seçili özelliklere göre eşleşen varyantı bul
  const matchedVariant = useMemo(() => {
    if (!hasVariants) return null;
    const keys = Object.keys(variantGroups);
    if (keys.length === 0) return null;
    return variants.find(v =>
      keys.every(k => !selectedAttrs[k] || v.ozellikler[k] === selectedAttrs[k])
        && keys.every(k => selectedAttrs[k] ? v.ozellikler[k] === selectedAttrs[k] : true)
        && Object.keys(selectedAttrs).length === keys.length
        && keys.every(k => selectedAttrs[k])
    ) || null;
  }, [selectedAttrs, variants, variantGroups, hasVariants]);

  // Aktif fiyat ve stok
  const activePrice = matchedVariant?.fiyat ?? selectedVariant?.fiyat ?? product.price;
  const activeStock = matchedVariant?.stok_miktari ?? selectedVariant?.stok_miktari ?? product.stock;
  const activeImage = matchedVariant?.gorsel_url || selectedVariant?.gorsel_url || null;

  const discount = product.original_price
    ? Math.round((1 - product.price / product.original_price) * 100)
    : null;

  const handleSelectAttr = (key: string, val: string) => {
    const newAttrs = { ...selectedAttrs, [key]: val };
    setSelectedAttrs(newAttrs);

    // Tam eşleşen varyantı bul - tüm varyant grupları için
    const keys = Object.keys(variantGroups);
    
    // Yeni attrs'te tüm key'ler dolu mu kontrol et
    const allKeysFilled = keys.every(k => newAttrs[k]);
    
    if (allKeysFilled) {
      // Tüm özellikler seçilmiş, eşleşen varyantı bul
      const matched = variants.find(v =>
        keys.every(k => v.ozellikler[k] === newAttrs[k])
      );
      
      setSelectedVariant(matched || null);

      // Eşleşen varyantın bir ürün slug'ı varsa ve farklıysa o ürüne git
      if (matched && matched.urun_slug && matched.urun_slug !== product.slug) {
        navigate(`/urun/${matched.urun_slug}`);
        return; // Navigate sonrası state güncellemesi yapma
      }
    } else {
      // Henüz tüm key'ler seçilmemiş, sadece state güncelle
      setSelectedVariant(null);
    }
  };

  // Belirli bir özellik değerinin seçilebilir olup olmadığını kontrol et
  const isAttrAvailable = (key: string, val: string) => {
    return variants.some(v => {
      const otherKeys = Object.keys(variantGroups).filter(k => k !== key);
      return v.ozellikler[key] === val &&
        otherKeys.every(k => !selectedAttrs[k] || v.ozellikler[k] === selectedAttrs[k]);
    });
  };

  const allAttrsSelected = hasVariants
    ? Object.keys(variantGroups).every(k => selectedAttrs[k])
    : true;

  const handleAddToCart = () => {
    if (hasVariants && !allAttrsSelected) return;
    if (activeStock <= 0) return;

    for (let i = 0; i < quantity; i++) {
      addItem({
        id: matchedVariant ? matchedVariant.id : product.id,
        product_id: product.id,
        name: product.name,
        price: activePrice,
        image_url: activeImage || product.image_url,
        slug: product.slug,
        weight: product.weight || undefined,
        variant_id: matchedVariant?.id,
        variant_name: matchedVariant?.varyant_adi,
        stock: activeStock,
      });
    }
    setAdded(true);
    setTimeout(() => setAdded(false), 2200);
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => {
      const filled = i < Math.floor(rating);
      const half = !filled && i < rating;
      return (
        <div key={i} className="w-4 h-4 flex items-center justify-center">
          <i className={`text-sm ${filled ? 'ri-star-fill text-gold' : half ? 'ri-star-half-fill text-gold' : 'ri-star-line text-cream-dark'}`} />
        </div>
      );
    });
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Category & Breadcrumb */}
      {product.category_name && (
        <Link
          to={`/urunler?kategori=${encodeURIComponent(product.category_name)}`}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-gold uppercase tracking-widest hover:text-gold-dark transition-colors w-fit"
        >
          <div className="w-3 h-3 flex items-center justify-center">
            <i className="ri-price-tag-3-line text-xs" />
          </div>
          {product.category_name}
        </Link>
      )}

      {/* Title */}
      <h1 className="font-serif text-3xl lg:text-4xl font-bold text-coffee leading-tight">
        {product.name}
      </h1>

      {/* Rating Row */}
      <div className="flex items-center gap-3 flex-wrap">
        {product.review_count > 0 ? (
          <>
            <div className="flex items-center gap-0.5">
              {renderStars(product.rating)}
            </div>
            <span className="text-sm font-semibold text-coffee">{product.rating.toFixed(1)}</span>
            <span className="text-sm text-coffee-light">
              ({product.review_count} yorum)
            </span>
          </>
        ) : (
          <span className="text-sm text-coffee-light">Henüz yorum yapılmamış</span>
        )}
        {product.weight && (
          <>
            <span className="text-cream-dark">·</span>
            <span className="inline-flex items-center gap-1 text-xs font-medium text-coffee-light bg-cream px-2.5 py-1 rounded-full">
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-scales-2-line text-xs" />
              </div>
              {product.weight}
            </span>
          </>
        )}
      </div>

      {/* Short Description */}
      {product.short_description && (
        <p className="text-coffee-light leading-relaxed text-[15px]">{product.short_description}</p>
      )}

      {/* Divider */}
      <div className="border-t border-cream-dark" />

      {/* Price Block */}
      <div className="flex items-end gap-3">
        <span className="font-serif text-4xl font-bold text-coffee tracking-tight">
          {activePrice.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}₺
        </span>
        {product.original_price && !matchedVariant && (
          <div className="flex items-center gap-2 pb-1">
            <span className="text-lg text-coffee-light line-through">
              {product.original_price.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}₺
            </span>
            <span className="px-2.5 py-0.5 bg-red-50 text-red-600 text-xs font-bold rounded-full border border-red-100">
              %{discount} tasarruf
            </span>
          </div>
        )}
      </div>

      {/* Stock Status */}
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${activeStock > 0 ? 'bg-green-500' : 'bg-red-400'}`} />
        <span className={`text-sm font-medium ${activeStock > 0 ? 'text-green-700' : 'text-red-500'}`}>
          {activeStock > 10
            ? 'Stokta mevcut'
            : activeStock > 0
            ? `Son ${activeStock} ürün!`
            : 'Stok tükendi'}
        </span>
        {activeStock > 0 && activeStock <= 5 && (
          <span className="text-xs text-warm-orange font-medium">— Hızlı al!</span>
        )}
      </div>

      {/* Variant Selectors */}
      {hasVariants && Object.entries(variantGroups).map(([key, values]) => (
        <div key={key} className="flex flex-col gap-2.5">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-coffee">{key}</span>
            {selectedAttrs[key] && (
              <span className="text-sm text-coffee-light">— {selectedAttrs[key]}</span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {values.map(val => {
              const available = isAttrAvailable(key, val);
              const selected = selectedAttrs[key] === val;
              return (
                <button
                  key={val}
                  onClick={() => available && handleSelectAttr(key, val)}
                  disabled={!available}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border-2 transition-all duration-150 whitespace-nowrap cursor-pointer
                    ${selected
                      ? 'border-coffee bg-coffee text-white'
                      : available
                      ? 'border-cream-dark bg-white text-coffee hover:border-coffee-light'
                      : 'border-cream-dark bg-cream text-coffee-light opacity-50 cursor-not-allowed line-through'
                    }`}
                >
                  {val}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {/* Variant uyarısı */}
      {hasVariants && !allAttrsSelected && (
        <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2 border border-amber-100">
          Lütfen {Object.keys(variantGroups).filter(k => !selectedAttrs[k]).join(', ')} seçiniz
        </p>
      )}

      {/* Quantity + Add to Cart */}
      <div className="flex items-stretch gap-3">
        {/* Quantity */}
        <div className="flex items-center bg-cream rounded-xl border border-cream-dark overflow-hidden h-12">
          <button
            onClick={() => setQuantity(q => Math.max(1, q - 1))}
            className="w-11 h-12 flex items-center justify-center text-coffee-light hover:text-coffee hover:bg-cream-dark transition-colors cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-subtract-line text-base" />
            </div>
          </button>
          <span className="w-10 text-center text-base font-semibold text-coffee">{quantity}</span>
          <button
            onClick={() => setQuantity(q => activeStock ? Math.min(activeStock, q + 1) : q + 1)}
            className="w-11 h-12 flex items-center justify-center text-coffee-light hover:text-coffee hover:bg-cream-dark transition-colors cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line text-base" />
            </div>
          </button>
        </div>

        {/* Add to Cart */}
        <button
          onClick={handleAddToCart}
          disabled={activeStock === 0 || (hasVariants && !allAttrsSelected)}
          className={`flex-1 py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
            added
              ? 'bg-green-500 text-white scale-[0.98]'
              : 'bg-coffee hover:bg-coffee-medium text-white'
          }`}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`text-base ${added ? 'ri-check-line' : 'ri-shopping-bag-line'}`} />
          </div>
          {added ? 'Sepete Eklendi!' : activeStock === 0 ? 'Stok Tükendi' : 'Sepete Ekle'}
        </button>

        {/* Wishlist */}
        <button
          onClick={toggleFavorite}
          disabled={favLoading}
          className={`w-12 h-12 flex items-center justify-center rounded-xl border-2 transition-all duration-200 cursor-pointer disabled:opacity-50 ${
            wished
              ? 'border-red-300 bg-red-50 text-red-500'
              : 'border-cream-dark hover:border-coffee-light text-coffee-light hover:text-coffee'
          }`}
          aria-label="Favorilere ekle"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            {favLoading ? (
              <i className="ri-loader-4-line animate-spin text-lg" />
            ) : (
              <i className={`text-lg ${wished ? 'ri-heart-fill' : 'ri-heart-line'}`} />
            )}
          </div>
        </button>
      </div>

      {/* Quick add note */}
      {added && (
        <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 rounded-xl px-4 py-3 border border-green-100">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-checkbox-circle-line text-base" />
          </div>
          <span>
            <strong>{quantity}</strong> adet
            {matchedVariant ? ` (${matchedVariant.varyant_adi})` : ''} sepete eklendi!
          </span>
        </div>
      )}
    </div>
  );
}
